// SME chat.service.ts
import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { fromEvent, Observable, map } from 'rxjs';

/**
 * @service SMEChatService
 * @description
 * Manages real-time chat communication with the backend using Socket.IO. This service encapsulates
 * all socket-related logic, providing a clean, observable-based API for components to interact with
 * the chat server.
 *
 * **Key Responsibilities:**
 * 1.  **Connection Management**: Handles the lifecycle of the socket connection, including connecting, disconnecting, and reconnecting.
 * 2.  **Authentication**: Provides a method to send login credentials to the socket server to authenticate the user's session.
 * 3.  **Messaging**: Allows sending and receiving text messages and attachments.
 * 4.  **Event Handling**: Exposes various socket events (e.g., 'connect', 'disconnect', 'getMessage') as RxJS Observables.
 * 5.  **Custom Events**: Supports sending and receiving custom-named events for flexible communication protocols.
 *
 * **Data Flow:**
 * - A component calls `connect()` to initialize the socket connection.
 * - The component subscribes to `onConnected()` to know when the connection is established.
 * - Once connected, the component calls `login()` with user credentials.
 * - The component can then `sendMessage()` or `onSendAttachment()` and subscribe to `getMessage()` or `onGetAttachment()` to participate in the chat.
 * - When the component is destroyed, it should call `disconnect()` to close the socket and prevent memory leaks.
 *
 * **Features:**
 * - Singleton socket instance to prevent multiple connections.
 * - RxJS integration for reactive event handling.
 * - Support for read receipts (`onReadMessages`).
 * - Generic methods for handling custom application-specific events.
 *
 * @architecture
 * This service is provided in the root injector, making it a singleton available throughout the application.
 * It uses the `socket.io-client` library for the underlying communication and wraps its event-emitter pattern
 * into RxJS Observables using `fromEvent`.
 *
 * @service-endpoint https://www.aqucloud.com:9000
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({ providedIn: 'root' })
export class SMEChatService {

  /**
   * @private
   * @type {Socket}
   * @description The underlying Socket.IO client instance. It is initialized on the first call to `connect()`.
   */
  private socket!: Socket;
  
  /**
   * @method connect
   * @description Establishes a connection to the Socket.IO server.
   * It ensures that only one socket connection is created by checking if `this.socket` already exists.
   * Configures the connection with specific transport options and custom headers for authentication/identification.
   *
   * @returns {void}
   * @side-effect Initializes the `this.socket` property and initiates a network connection to the chat server.
   */
  connect(): void {
    if (!this.socket) {
      this.socket = io('https://www.aqucloud.com:9000', {
        transports: ['polling'],
        withCredentials: true,
        extraHeaders: { source: 'client_web:Ipro7$socket#' }
      });
    }
  }

  /**
   * @method onConnected
   * @description Provides an RxJS Observable stream that emits when the socket successfully connects to the server.
   *
   * @returns {Observable<any>} An Observable that emits the 'connect' event object.
   * @example
   * this.smeChatService.onConnected().subscribe(() => {
   *   console.log('Socket connected!');
   *   this.smeChatService.login({ userId: 'user123' });
   * });
   */
  onConnected(): Observable<any> {
    return fromEvent(this.socket, 'connect');
  }

  /**
   * @method login
   * @description Emits a 'login' event to the server with the user's authentication payload.
   * This should be called after a successful connection is established.
   *
   * @param {any} payload - The authentication data, typically containing user ID or a session token.
   * @returns {void}
   * @side-effect Sends data over the socket connection.
   */
  login(payload: any): void {
    this.socket.emit('login', payload);
  }

  /**
   * @method onReconnect
   * @description Provides an Observable stream that emits when the socket automatically reconnects after a disconnection.
   *
   * @returns {Observable<any>} An Observable that emits on the 'reconnect' event.
   */
  onReconnect(): Observable<any> {
    return fromEvent(this.socket, 'reconnect');
  }

  /**
   * @method sendMessage
   * @description Emits a 'sendMessage' event to the server to send a text-based chat message.
   *
   * @param {any} data - The message payload, typically an object containing the message text and recipient info.
   * @returns {void}
   * @side-effect Sends data over the socket connection.
   */
  sendMessage(data: any): void {
    this.socket.emit('sendMessage', data);
  }

  /**
   * @method getMessage
   * @description Provides an Observable stream of incoming chat messages from the server via the 'getMessage' event.
   *
   * @returns {Observable<any>} An Observable that emits message data received from the server.
   * @example
   * this.smeChatService.getMessage().subscribe(message => {
   *   this.chatMessages.push(message);
   * });
   */
  getMessage(): Observable<any> {
    return fromEvent(this.socket, 'getMessage');
  }

   /**
   * @method onReadMessages
   * @description Emits an 'isRead' event to the server to mark messages as read.
   * Includes a safeguard to warn if the socket is not connected when called.
   *
   * @param {any} data - The payload identifying which messages to mark as read (e.g., conversation ID).
   * @returns {void}
   * @side-effect Sends data over the socket connection.
   */
  onReadMessages(data: any): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('Socket not ready, queueing read receipt');
      return;
    }
    this.socket.emit('isRead', data);
  }

  /**
   * @method onSendAttachment
   * @description Emits a 'sendAttachment' event to the server to send a file or media.
   *
   * @param {any} data - The attachment payload, typically containing file data (e.g., as a Base64 string) and metadata.
   * @returns {void}
   * @side-effect Sends data over the socket connection.
   */
  onSendAttachment = (data: any) => {
    this.socket.emit('sendAttachment', data);
  };

  /**
   * @method onGetCustomEventData
   * @description Provides a generic way to listen for any custom event from the server.
   *
   * @param {string} eventName - The name of the custom event to listen for.
   * @returns {Observable<any>} An Observable stream for the specified event, with the data mapped from the event payload.
   * @example
   * // Listen for typing indicators
   * this.smeChatService.onGetCustomEventData('user-typing').subscribe(typingData => {
   *   console.log(`${typingData.userName} is typing...`);
   * });
   */
  onGetCustomEventData = (eventName: string) => {
    return fromEvent(this.socket, eventName).pipe(map((data: any) => data));
  };

  /**
   * @method onGetAttachment
   * @description Provides an Observable stream of incoming attachments from the server via the 'getAttachment' event.
   *
   * @returns {Observable<any>} An Observable that emits attachment data received from the server.
   */
  onGetAttachment = () => {
    return fromEvent(this.socket, 'getAttachment').pipe(map((data: any) => data));
  };

  /**
   * @method onSendCustomEventData
   * @description Provides a generic way to emit any custom event to the server.
   *
   * @param {string} eventName - The name of the custom event to emit.
   * @param {any} data - The payload to send with the event.
   * @returns {void}
   * @side-effect Sends data over the socket connection.
   */
  onSendCustomEventData = (eventName: string, data: any) => {
    this.socket.emit(eventName, data);
  };

  /**
   * @method disconnect
   * @description Disconnects the socket from the server and cleans up the local socket instance.
   * This should be called when the chat feature is no longer needed (e.g., in `ngOnDestroy`).
   *
   * @returns {void}
   * @side-effect Terminates the network connection and resets the `this.socket` property.
   */
  disconnect(): void {
    this.socket?.disconnect();
    this.socket = undefined as any;
  }

  /**
   * @method onDisconnected
   * @description Provides an Observable stream that emits when the socket is disconnected from the server.
   *
   * @returns {Observable<any>} An Observable that emits on the 'disconnect' event.
   */
  onDisconnected(): Observable<any> {
    return fromEvent(this.socket, 'disconnect');
  }
}
